import React, { useMemo } from 'react'
import {
  TableContainer,
  Stack,
  Typography,
  Table,
  TableBody,
  useTheme,
  useMediaQuery,
} from '@mui/material'
import TestIds from '../../../testIds'
import { QuantityChip } from '../../dashboard/components/QuantityChip'
import {
  allSeedWeightForPaddock,
  allSeedsCount,
  roundedTo,
  totalPriceForPaddock,
} from '../utils/calculateTotal'
import { SeedTableHeader } from './SeedTableCell'
import { SeedOrderTableProductRow } from './SeedOrderTableProductRow'
import SeedOrderTableMobile from './SeedOrderTableMobile'
import SeedFormTitle from './SeedFormTitle'
import SeedOrderDeliveryDetails from './SeedOrderDeliveryDetails'
import MixTypeIcon from './MixTypeIcon'
import { SEED_MIX_TYPE, SeedMix, SeedOrdersInfo } from '../types/SeedOrderFormType'
import { colors } from '../../../themes/colors'
import { SeedStatsLabel } from './SeedStatsLabel'
import { NotesFormReadOnly } from '../form/NotesForm'

export interface SeedMixTableProps {
  seed: SeedMix
}

export const SeedMixTable = ({ seed }: SeedMixTableProps) => {
  const theme = useTheme()
  const isSm = useMediaQuery(theme.breakpoints.down('sm'))

  const totalSeeds = useMemo(() => allSeedsCount(seed.products), [seed.products])
  const warning = useMemo(() => {
    if (totalSeeds === 1 && seed.mixType === SEED_MIX_TYPE.CUSTOM) {
      return 'This custom seed mix has only 1 product'
    }
    return undefined
  }, [totalSeeds, seed])

  if (isSm) {
    return <SeedOrderTableMobile seed={seed} />
  }

  return seed.paddocks.map((paddock) => {
    const weight = allSeedWeightForPaddock(seed.products, paddock)
    const sowingRate = weight / paddock.hectares
    const pricePerHa = totalPriceForPaddock(seed.products, paddock)
    return (
      <div key={`${seed.id}-${paddock.name}`}>
        {warning && (
          <Typography
            variant='body2'
            color={theme.colors.error500}
            mt={0.5}
            data-testid={TestIds.feat.seed.product.singleSeedInCustomSeed}
          >
            {warning}
          </Typography>
        )}
        <Stack
          direction='row'
          alignItems='center'
          justifyContent='space-between'
          p={2}
          borderBottom={1}
          borderColor={colors.primary100}
        >
          <Stack direction='row' alignItems='center' spacing={1}>
            <Typography variant='h6' color={theme.colors.primary500} fontWeight='bold'>
              {paddock.name}
            </Typography>
            <QuantityChip label={`${paddock.hectares} Ha`} />
            <QuantityChip label={`${allSeedsCount(seed.products)} products`} />
            {seed.mixType === SEED_MIX_TYPE.PREMIX ? null : (
              <QuantityChip label={`${roundedTo(weight)}kg`} />
            )}
          </Stack>
          <Stack direction='row' alignItems='center' spacing={2}>
            {seed.mixType === SEED_MIX_TYPE.PREMIX ? null : (
              <SeedStatsLabel title='Sowing Rate' value={`${roundedTo(sowingRate)} kg/Ha`} />
            )}
            <SeedStatsLabel title='Price per ha' value={`$${pricePerHa}`} />
          </Stack>
        </Stack>
        <Table aria-label='Seed orders table'>
          <SeedTableHeader mixType={seed.mixType} />
          <TableBody>
            {seed.products.map((seedProduct) => (
              <SeedOrderTableProductRow
                key={`${seedProduct.lineId}=${paddock.name}`}
                mixType={seed.mixType}
                paddock={paddock}
                seedProduct={seedProduct}
              />
            ))}
          </TableBody>
        </Table>
      </div>
    )
  })
}

export const SeedOrderTable = ({ seedOrder }: { seedOrder: SeedOrdersInfo }) => {
  return (
    <TableContainer data-testid={TestIds.feat.agreement.salesOrder.table}>
      {seedOrder?.mixes.map((seed, index) => {
        return (
          <Stack
            key={seed.id}
            bgcolor={colors.shade0}
            mt={1}
            sx={{ breakBefore: index === 0 ? 'avoid' : 'page' }}
          >
            <SeedFormTitle title={seed.name} icon={<MixTypeIcon type={seed.mixType} />}>
              <SeedOrderDeliveryDetails seed={seed} />
            </SeedFormTitle>
            <SeedMixTable key={seed.id} seed={seed} />
            <NotesFormReadOnly
              internalNotes={seed.internalNotes}
              externalNotes={seed.externalNotes}
            />
          </Stack>
        )
      })}
    </TableContainer>
  )
}
